import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, BarChart } from './_finHelpers';

// RMD Calculator — Required Minimum Distribution per IRS Publication 590-B (Uniform Lifetime Table)
export default function RMDCalc() {
  const [birthYear, setBirthYear] = useState(1951);
  const [rmdYear, setRmdYear] = useState(2026);
  const [balance, setBalance] = useState(300000);
  const [spouseBeneficiary, setSpouseBeneficiary] = useState('yes');
  const [spouseBirth, setSpouseBirth] = useState(1953);
  const [rate, setRate] = useState(5);

  // IRS 2022+ Uniform Lifetime Table (age -> distribution period)
  const uniformTable = {
    72: 27.4, 73: 26.5, 74: 25.5, 75: 24.6, 76: 23.7, 77: 22.9, 78: 22.0, 79: 21.1, 80: 20.2,
    81: 19.4, 82: 18.5, 83: 17.7, 84: 16.8, 85: 16.0, 86: 15.2, 87: 14.4, 88: 13.7, 89: 12.9,
    90: 12.2, 91: 11.5, 92: 10.8, 93: 10.1, 94: 9.5, 95: 8.9, 96: 8.4, 97: 7.8, 98: 7.3,
    99: 6.8, 100: 6.4, 101: 6.0, 102: 5.6, 103: 5.2, 104: 4.9, 105: 4.6, 106: 4.3, 107: 4.1,
    108: 3.9, 109: 3.7, 110: 3.5, 111: 3.4, 112: 3.3, 113: 3.1, 114: 3.0, 115: 2.9, 116: 2.8,
    117: 2.7, 118: 2.5, 119: 2.3, 120: 2.0,
  };
  const getPeriod = (age) => uniformTable[age] || (age < 72 ? uniformTable[72] : uniformTable[120]);

  const c = useMemo(() => {
    const startAge = rmdYear - birthYear;
    const period = getPeriod(startAge);
    const rmd = balance / period;
    // Project forward
    const rows = [];
    let bal = balance;
    for (let y = 0; y < 15; y++) {
      const a = startAge + y;
      const p = getPeriod(a);
      const yrRmd = bal / p;
      const endBal = (bal - yrRmd) * (1 + rate/100);
      rows.push({ year: rmdYear + y, age: a, period: p, rmd: yrRmd, balance: endBal });
      bal = endBal;
    }
    return { rmd, period, startAge, rows };
  }, [birthYear, rmdYear, balance, rate]);
  return (
    <CalcShell look="cnet" title="RMD Calculator" subtitle="Compute your Required Minimum Distribution (IRS Publication 590-B — Uniform Lifetime Table)."
      about={<><p><b>Required Minimum Distributions (RMDs)</b> are mandatory annual withdrawals from tax-deferred retirement accounts starting at age 73 (as of SECURE Act 2.0). The RMD equals your prior-year-end balance divided by the IRS life-expectancy factor.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Your year of birth</label><input type="number" value={birthYear} onChange={e=>setBirthYear(+e.target.value||0)}/></div>
          <div className="field"><label>Year of RMD</label><input type="number" value={rmdYear} onChange={e=>setRmdYear(+e.target.value||0)}/></div>
          <div className="field"><label>Account balance as of 12/31/{rmdYear-1}</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={balance} onChange={e=>setBalance(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Is your spouse the primary beneficiary?</label>
            <div className="flex gap-4 mt-1"><label className="flex items-center gap-1"><input type="radio" checked={spouseBeneficiary==='yes'} onChange={()=>setSpouseBeneficiary('yes')}/> Yes</label><label className="flex items-center gap-1"><input type="radio" checked={spouseBeneficiary==='no'} onChange={()=>setSpouseBeneficiary('no')}/> No</label></div>
          </div>
          {spouseBeneficiary === 'yes' && <div className="field"><label>Your spouse's date of birth</label><input type="number" value={spouseBirth} onChange={e=>setSpouseBirth(+e.target.value||0)}/></div>}
          <div className="field"><label>Estimated rate of return (Optional)</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Your RMD for {rmdYear} is</div><div className="result-value" data-testid="rmd-result">{fmt(c.rmd)}</div></div>
          <p className="text-sm text-slate-600 mb-2">The distribution period for your case is: <b>{c.period}</b></p>
          <p className="text-sm text-slate-600 font-mono">RMD = ${balance.toLocaleString()} / {c.period} = <b>{fmt(c.rmd)}</b></p>
          <p className="text-xs text-slate-500 mt-4">If you only withdraw the RMD at the end of each year and your return rate is {rate}% per year, your future account balance and RMDs will look like the following.</p>
        </div>
      </div>
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">RMD and Balance Projection</h3>
        <BarChart
          data={c.rows.map(r => ({
            label: String(r.age),
            values: [
              { value: r.balance, color: '#2563eb' },
              { value: r.rmd,     color: '#f97316' },
            ]
          }))}
          stack={false}
          legend={[
            { label: 'End of Year Balance', color: '#2563eb' },
            { label: 'Annual RMD', color: '#f97316' },
          ]}
        />
      </div>

      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Projected RMDs and Balances</h3>
        <table className="w-full text-sm">
          <thead className="bg-blue-800 text-white"><tr><th className="px-3 py-2 text-left">Year</th><th className="px-3 py-2 text-left">Your Age</th><th className="px-3 py-2 text-right">Distribution period</th><th className="px-3 py-2 text-right">RMD</th><th className="px-3 py-2 text-right">End of Year Balance</th></tr></thead>
          <tbody>
            {c.rows.map((r,i)=>(<tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}><td className="px-3 py-2">{r.year}</td><td className="px-3 py-2">{r.age}</td><td className="px-3 py-2 text-right">{r.period}</td><td className="px-3 py-2 text-right text-orange-600">{fmt(r.rmd)}</td><td className="px-3 py-2 text-right font-semibold">{fmt(r.balance)}</td></tr>))}
          </tbody>
        </table>
      </div>
    </CalcShell>
  );
}
